import { Boolean } from '@model/Types';
import { between } from '@packages/functions';
import { getState } from '@redux-src/state';
import classNames$ from 'classnames';
import { uniq, unset } from 'lodash';

export { isDevelopment, isEnvironment, isProduction } from '@apparel/core/environment/check';
export * from '@apparel/core/platform/checks';
//  KEEP IT AT THE END
export { between };

//  Stripe fees
export const STRIPE_CC_FIXED = 0.3;
export const STRIPE_CC_PERCENT = 0.029;
export const STRIPE_AFTERPAY_PERCENT = 0.06;
export const STRIPE_ACH_FIXED = 5;
export const STRIPE_ACH_PERCENT = 0.008;

export const classNames = (...args: any) =>
    uniq(
        classNames$(...args)
            .replace(/\s\s/g, ' ')
            .split(' '),
    ).join(' ');

export const classList = classNames;

/**
 * Remove some keys from an object
 * @param props
 * @param keys
 * @returns {*}
 */
export const cleanProps = (props: any, ...keys: any[]) =>
    keys.forEach((key) => {
        if (Array.isArray(key)) {
            cleanProps(props, ...key);
        } else {
            unset(props, key);
        }
    });

/**
 * Adds item to array at given idx position, returns new array
 * @param array
 * @param idx
 * @param item
 * @returns {*[]}
 */
export const insertArrayItem = (array: any[], item: any, idx = 0) => [
    ...array.slice(0, idx),
    item,
    ...array.slice(idx),
];

/**
 * Removes element from array, by the given IDX and returns new array
 * @param array
 * @param idx
 * @returns array
 */
export const removeArrayItem = (array: any[], idx: number) => [...array.slice(0, idx), ...array.slice(idx + 1)];

/**
 * Removes element from array, by the given IDX, adds item to IDX and returns new array
 * @param array
 * @param idx
 * @param item
 * @returns {*[]}
 */
export const replaceArrayItem = (array: any[], item: any, idx: number) => {
    return insertArrayItem(removeArrayItem(array, idx), item, idx);
};

/**
 * Toggles the value in the given array and returns new array, used for toggling IDs or types stored as list
 * @example
 * toggleElementInArray([1,2,3], 2) //  returns [1,3]
 * toggleElementInArray([1,3], 2) //  returns [1,2,3]
 *
 * @param array
 * @param element
 * @returns array
 */
export const toggleElementInArray = (array: any[], element: any) => {
    const idx = array.indexOf(element);

    return idx === -1 ? insertArrayItem(array, element, 0) : removeArrayItem(array, idx);
};

/**
 * Get object filtered of empty and excepted value
 * @example
 * filterObjectExceptValue({q: null, f: 'test', s: '-'}, '-')  //  result {f: 'test'}
 * @param object
 * @param exceptValue
 * @returns {{}}
 */
export const filterObjectExceptValue = (object: any, exceptValue: any = '-') => {
    return Object.keys(object)
        .filter((key) => object[key] !== exceptValue && object[key] !== null && typeof object[key] !== 'undefined')
        .reduce((obj, key) => {
            obj[key] = object[key];
            return obj;
        }, {});
};

/**
 * Check if value is true
 * @param val
 * @returns {*}
 */
export const isTrue = (val: any): boolean => Boolean.toValue(val);

export const getNumberToDecimalPlaces = (value: number, places: number) => {
    const pow = Math.pow(10, places);
    return Math.round(value * pow) / pow;
};

/**
 * Calculate Stripe Fee
 * @param {number} paymentMethodID - Payment Method
 * @param {number} amount - Amount to calculate the fee out of
 * @returns {float}
 */
export const getTransactionFees = (paymentMethodID, amount) => {
    paymentMethodID = Number(paymentMethodID);
    amount = Number(amount);

    if (paymentMethodID === 1 || paymentMethodID === 9 || paymentMethodID === 10) {
        return (amount + STRIPE_CC_FIXED) / (1 - STRIPE_CC_PERCENT) - amount;
    }
    if (paymentMethodID === 11) {
        return STRIPE_CC_FIXED + amount * STRIPE_AFTERPAY_PERCENT;
    }

    if (paymentMethodID === 3 || paymentMethodID === 4 || paymentMethodID === 13) {
        if (amount >= 625) {
            return STRIPE_ACH_FIXED;
        } else {
            return amount * STRIPE_ACH_PERCENT;
        }
    } else if (paymentMethodID === 7) {
        return (amount + 0.3) / (1 - 0.044) - amount;
    }
    return 0;
};

export const getMOQ = (item, isInternationalOrder = false) => {
    if (isInternationalOrder) {
        return item?.international_minimum_order_quantity > 1
            ? item?.international_minimum_order_quantity
            : Math.max(item?.minimum_order_quantity, 1);
    }
    return Math.max(item?.minimum_order_quantity, 1);
};

export const internationalOrder = (location) => {
    if (location === null) {
        return false;
    }
    return location?.country_name !== 'United States';
};
export const getOrderTotalAndTax = (prices$, creditToUse, useCredit, couponDiscount, salesTaxFormula, hasPayment) => {
    let total: number;
    if (typeof prices$.total !== 'undefined' && useCredit && typeof creditToUse !== 'undefined') {
        total =
            prices$.subtotal -
            getNumberToDecimalPlaces(couponDiscount, 2) +
            prices$.shipping_cost +
            getNumberToDecimalPlaces(prices$.shipping_method_adds, 2) -
            getNumberToDecimalPlaces(creditToUse, 2) -
            prices$.discount +
            getNumberToDecimalPlaces(prices$.addon_cost, 2);
    } else {
        total =
            prices$.subtotal -
            getNumberToDecimalPlaces(couponDiscount, 2) +
            prices$.shipping_cost +
            getNumberToDecimalPlaces(prices$.shipping_method_adds, 2) -
            prices$.discount +
            getNumberToDecimalPlaces(prices$.addon_cost, 2);
    }
    const tax = hasPayment ? prices$.tax : calculateTax(total, salesTaxFormula);
    return {
        total: getNumberToDecimalPlaces(total + tax, 2),
        tax: tax,
    };
};

export const calculateTax = (total: number, salesTax: any) => {
    if (!salesTax) {
        return 0;
    }

    const places = 2;
    if (salesTax.cost_type === 2) {
        const pow = Math.pow(10, places);
        const totalCents = Math.round(total * pow);
        const convertedTaxCostPercentage = Math.round(salesTax.cost * pow);
        const taxCents = Math.round((totalCents * convertedTaxCostPercentage) / Math.pow(pow, 2));
        return taxCents / pow;
    }
    return Math.max(0, getNumberToDecimalPlaces(salesTax.cost, places));
};

export const getOrderTotalDue$ = (total, creditToUse, totalAmountPaid) => {
    const totalAmt: any = getNumberToDecimalPlaces(total ?? 0, 4);
    const totalAmtPaid: any = getNumberToDecimalPlaces(totalAmountPaid ?? 0, 4);
    const dueAmt$ = totalAmt - totalAmtPaid;
    if ((creditToUse ?? 0) * -1 === dueAmt$) {
        return 0;
    }

    return getNumberToDecimalPlaces(dueAmt$, 2);
};

export const getSubdomainData = () => {
    return getState().Wholesaler.toJS().subdomainData ?? {};
};

export const checkWholesalerDomain = () => {
    const host = window.location.host;
    const subdomain = host.split('.')[0];

    return {
        host,
        subdomain,
        isGB: process.env.REACT_APP_URL?.includes(host),
    };
};

enum ORDER_TYPE {
    STANDARD_ORDER = 1,
    ADD_ON_ORDER = 2,
    REDO_ORDER = 3,
    RE_ORDER = 4,
    CUSTOM_SAMPLE = 5,
    REPEAT_ORDER = 7,
}

export const moqIsNotRequired = (orderTypeId: number): boolean => {
    return !!(
        orderTypeId === ORDER_TYPE.ADD_ON_ORDER ||
        orderTypeId === ORDER_TYPE.REDO_ORDER ||
        orderTypeId === ORDER_TYPE.CUSTOM_SAMPLE ||
        orderTypeId === ORDER_TYPE.RE_ORDER ||
        orderTypeId === ORDER_TYPE.REPEAT_ORDER
    );
};
